import api from './api.service';
import { ContactRequest, ColocAgency, ContactAction, AgencyGrantResult, AgencyOverview } from '../types/contact';

class ContactsService {
  // Liste les demandes de contact d'une coloc (owner-only, scopé côté API).
  // Par ID de coloc : le baseURL inclut déjà /api → /api/colocs/{id}/contacts.
  async listForColoc(colocId: string): Promise<ContactRequest[]> {
    const response = await api.get(`/colocs/${colocId}/contacts`);
    return response.data;
  }

  // L'owner crée un contact à la main (prospect hors formulaire public).
  async createContact(
    colocId: string,
    data: { name: string; email: string; phone?: string; message?: string }
  ): Promise<ContactRequest> {
    const response = await api.post(`/colocs/${colocId}/contacts`, data);
    return response.data;
  }

  // L'owner modifie un contact existant.
  async updateContact(
    contactId: string,
    data: { name?: string; email?: string; phone?: string; message?: string }
  ): Promise<ContactRequest> {
    const response = await api.put(`/contacts/${contactId}`, data);
    return response.data;
  }

  // L'owner supprime un contact (et son historique d'actions).
  async deleteContact(contactId: string): Promise<void> {
    await api.delete(`/contacts/${contactId}`);
  }

  // Agences ayant accès à cette coloc (pour le menu d'assignation).
  async listColocAgencies(colocId: string): Promise<ColocAgency[]> {
    const response = await api.get(`/colocs/${colocId}/agencies`);
    return response.data;
  }

  /** Toutes les agences du bailleur, avec le détail par colocation.
   *  Ne dépend PAS de la colocation sélectionnée. */
  async listOwnerAgencies(): Promise<AgencyOverview[]> {
    const response = await api.get('/agencies');
    return response.data;
  }

  /** Donne à une agence l'accès à une coloc, en créant son compte au besoin.
   *  Si l'adresse est déjà celle d'une agence, le compte est RÉUTILISÉ. */
  async grantAgency(
    colocId: string,
    data: { first_name: string; last_name: string; email: string; send_welcome: boolean },
  ): Promise<AgencyGrantResult> {
    const response = await api.post(`/colocs/${colocId}/agencies`, data);
    return response.data;
  }

  /** (Re)envoie à l'agence son lien de création de mot de passe. */
  async sendAgencyWelcome(colocId: string, agencyId: string): Promise<{ sent: boolean }> {
    const response = await api.post(
      `/colocs/${colocId}/agencies/${agencyId}/welcome-email`, {},
    );
    return response.data;
  }

  /** Retire définitivement l'accès et rend les contacts en cours au propriétaire. */
  async revokeAgency(colocId: string, agencyId: string): Promise<{ contactsDesassignes: number }> {
    const response = await api.delete(`/colocs/${colocId}/agencies/${agencyId}`);
    return response.data;
  }

  /** Gèle ou rétablit l'accès d'une agence à une coloc.
   *  Distinct de la révocation : rien n'est désassigné, tout revient au
   *  rétablissement. */
  async setAgencyAccess(colocId: string, agencyId: string, enabled: boolean): Promise<unknown> {
    const response = await api.put(
      `/colocs/${colocId}/agencies/${agencyId}/access`,
      { enabled },
    );
    return response.data;
  }

  async assign(contactId: string, agencyUserId: string, emailBody?: string): Promise<unknown> {
    const response = await api.post(`/contacts/${contactId}/assign`, {
      agency_user_id: agencyUserId,
      email_body: emailBody || null,
    });
    return response.data;
  }

  async relance(contactId: string, message?: string): Promise<unknown> {
    const response = await api.post(`/contacts/${contactId}/relance`, { message: message || null });
    return response.data;
  }

  async close(contactId: string): Promise<unknown> {
    const response = await api.post(`/contacts/${contactId}/close`, {});
    return response.data;
  }

  async getCcRecipients(agencyId: string): Promise<string[]> {
    const response = await api.get(`/agencies/${agencyId}/cc-recipients`);
    return response.data;
  }

  async setCcRecipients(agencyId: string, emails: string[]): Promise<string[]> {
    const response = await api.put(`/agencies/${agencyId}/cc-recipients`, { emails });
    return response.data;
  }

  // --- Espace agence (Lot 3) ---
  async listAgencyContacts(): Promise<ContactRequest[]> {
    const response = await api.get('/agency/contacts');
    return response.data;
  }

  async logAction(contactId: string, actionType: string, note?: string): Promise<unknown> {
    const response = await api.post(`/agency/contacts/${contactId}/actions`, {
      action_type: actionType,
      note: note || null,
    });
    return response.data;
  }

  async getActions(contactId: string): Promise<ContactAction[]> {
    const response = await api.get(`/contacts/${contactId}/actions`);
    return response.data;
  }

  /** Le propriétaire journalise une action sur son propre contact. */
  async logOwnerAction(contactId: string, actionType: string, note?: string): Promise<unknown> {
    const response = await api.post(`/contacts/${contactId}/actions`, {
      action_type: actionType,
      note: note || null,
    });
    return response.data;
  }
}

export const contactsService = new ContactsService();
